/** Orders asynchronous work. No DOM, HTTP, pricing rules or Stripe SDK calls here. */
export class CheckoutCoordinator {
    constructor({request, view, payment}) {
        Object.assign(this, {request, view, payment});
        this.queue = Promise.resolve();
        this.pending = 0;
        this.state = null;
    }
    run(work) {
        this.pending++;
        this.view.busy(true);
        const task = this.queue.then(async () => {
            this.view.message('');
            try { return await work(); }
            catch (error) {
                if (error.result?.order) await this.accept(error.result);
                this.view.message(error.message, true);
                throw error;
            } finally {
                this.pending--;
                this.view.busy(this.pending > 0);
            }
        });
        this.queue = task.catch(() => {});
        return task;
    }
    async accept(result) {
        this.state = result;
        this.view.render(result);
        this.view.busy(this.pending > 0);
        if (result.payment && result.ready) await this.payment.update(result.payment);
        else this.payment.clear();
    }
    load() { return this.run(async () => this.accept(await this.request('load'))); }
    mutate(action, data) {
        return this.run(async () => {
            try { await this.accept(await this.request(action, data)); }
            catch (error) {
                // Never repeat an uncertain mutation. Reload the confirmed order instead.
                if (!error.result?.order) {
                    try { await this.accept(await this.request('load')); } catch (_) { /* Keep the original error. */ }
                }
                throw error;
            }
        });
    }
    pay(address) {
        return this.run(async () => {
            const shownTotal = this.state?.order.total;
            await this.accept(await this.request('address', address));
            if (!this.state.ready) throw new Error('Please complete your address and check product availability before paying.');
            if (shownTotal !== undefined && shownTotal !== this.state.order.total) throw new Error('Your total has changed. Review the updated amount and click Pay now again.');
            const prepared = await this.request('prepare', {version: this.state.version});
            if (prepared.redirect) { this.view.finish(prepared.redirect); return; }
            try {
                await this.accept(prepared);
                await this.payment.confirm();
            } catch (error) {
                try { await this.request('release'); } catch (_) { /* A pending bank payment must remain locked. */ }
                throw error;
            }
        });
    }
}
